import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent } from '../components/ui/tabs';
import { 
  Utensils, Clock, ChevronLeft, Salad, Coffee, Moon, Apple,
  Info, User, AlertCircle, Loader2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MEAL_ICONS = {
  breakfast: Coffee,
  lunch: Utensils,
  dinner: Moon,
  snack: Apple
};

const MEAL_COLORS = {
  breakfast: 'bg-amber-100 text-amber-700 border-amber-200',
  lunch: 'bg-green-100 text-green-700 border-green-200',
  dinner: 'bg-purple-100 text-purple-700 border-purple-200',
  snack: 'bg-blue-100 text-blue-700 border-blue-200'
};

const DAYS_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = {
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
  sunday: 'Sun'
};

export default function MealPlanPage() {
  const navigate = useNavigate();
  const [mealPlan, setMealPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(getCurrentDay());

  function getCurrentDay() {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return days[new Date().getDay()];
  }

  useEffect(() => {
    fetchMealPlan();
  }, []);

  const fetchMealPlan = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/my-meal-plan`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMealPlan(data);
      }
    } catch (error) {
      console.error('Failed to fetch meal plan:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  if (!mealPlan?.has_meal_plan) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white p-4">
        <div className="max-w-2xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-4"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <Card className="border-0 shadow-lg">
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Salad className="w-8 h-8 text-gray-400" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">No Meal Plan Yet</h2>
              <p className="text-gray-600 mb-4">
                Your dietitian hasn't assigned a meal plan yet. Check back later or contact your care team.
              </p>
              <Button onClick={() => navigate('/dashboard')}>
                Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const { template_name, calorie_level, cuisine_type, week_plan, general_notes, dietitian_name, dietitian_notes, start_date } = mealPlan;

  // Sort week_plan by day order
  const sortedWeekPlan = [...(week_plan || [])].sort((a, b) => 
    DAYS_ORDER.indexOf(a.day) - DAYS_ORDER.indexOf(b.day)
  );

  const selectedDayPlan = sortedWeekPlan.find(d => d.day === selectedDay);

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white pb-20" data-testid="meal-plan-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-500 to-emerald-400 text-white p-4 pb-8">
        <div className="max-w-2xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-white hover:bg-white/10 -ml-2 mb-2"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold">{template_name}</h1>
              <div className="flex items-center gap-2 mt-2">
                <Badge variant="secondary" className="bg-white/20 text-white">
                  {calorie_level} kcal
                </Badge>
                <Badge variant="secondary" className="bg-white/20 text-white capitalize">
                  {cuisine_type}
                </Badge>
              </div>
            </div>
            <Salad className="w-12 h-12 text-white/30" />
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 -mt-4">
        {/* Day Tabs - Scrollable */}
        <Card className="border-0 shadow-lg mb-4">
          <CardContent className="p-2">
            <Tabs value={selectedDay} onValueChange={setSelectedDay}>
              <div 
                className="flex w-full h-auto p-1 bg-gray-100 rounded-lg overflow-x-auto gap-1"
                style={{ 
                  scrollbarWidth: 'none', 
                  msOverflowStyle: 'none',
                  WebkitOverflowScrolling: 'touch'
                }}
              >
                {DAYS_ORDER.map(day => {
                  const isToday = day === getCurrentDay();
                  const dayPlan = sortedWeekPlan.find(d => d.day === day);
                  const isSelected = selectedDay === day;
                  return (
                    <button 
                      key={day} 
                      onClick={() => setSelectedDay(day)}
                      className={`py-2 px-3 text-xs font-medium flex-shrink-0 min-w-[44px] rounded-md transition-all ${
                        isSelected 
                          ? 'bg-white text-gray-900 shadow' 
                          : 'text-gray-600 hover:bg-white/50'
                      } ${isToday ? 'ring-2 ring-green-500 ring-offset-1' : ''}`}
                      data-testid={`day-tab-${day}`}
                    >
                      <div className="flex flex-col items-center">
                        <span>{DAY_LABELS[day]}</span>
                        {dayPlan?.is_reward_day && (
                          <span className="text-[10px] text-orange-500">🎉</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </Tabs>
          </CardContent>
        </Card>

        {/* Dietitian Notes */}
        {dietitian_notes && (
          <Card className="border-0 shadow-md mb-4 border-l-4 border-orange-400">
            <CardContent className="py-3">
              <div className="flex items-start gap-2">
                <User className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-gray-700">{dietitian_notes}</p>
                  <p className="text-xs text-gray-500 mt-1">— {dietitian_name}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Day's Meals */}
        {selectedDayPlan ? (
          <div className="space-y-4">
            {selectedDayPlan.is_reward_day && (
              <Card className="border-0 shadow-md bg-gradient-to-r from-orange-100 to-amber-100">
                <CardContent className="py-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">🎉</span>
                    <div>
                      <p className="font-medium text-orange-800">Reward Day!</p>
                      <p className="text-sm text-orange-600">Enjoy your day but keep portions moderate.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {selectedDayPlan.meals.map((meal, index) => {
              const Icon = MEAL_ICONS[meal.meal_type] || Utensils;
              const colorClass = MEAL_COLORS[meal.meal_type] || 'bg-gray-100 text-gray-700 border-gray-200';

              return (
                <Card key={index} className="border-0 shadow-md" data-testid={`meal-card-${meal.meal_type}`}>
                  <CardHeader className={`${colorClass} pb-3`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Icon className="w-5 h-5" />
                        <div>
                          <CardTitle className="text-base capitalize">{meal.meal_type}</CardTitle>
                          <p className="text-xs opacity-75 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {meal.time_range}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <ul className="space-y-3">
                      {meal.items.map((item, itemIndex) => (
                        <li 
                          key={itemIndex}
                          className="flex items-start justify-between"
                        >
                          <div className="flex-1">
                            <span className={`${item.is_optional ? 'text-gray-500' : 'text-gray-800'}`}>
                              {item.food_name}
                            </span>
                            {item.is_optional && (
                              <Badge variant="outline" className="ml-2 text-xs">Optional</Badge>
                            )}
                            {item.notes && (
                              <p className="text-xs text-gray-500 mt-1">{item.notes}</p>
                            )}
                          </div>
                          <Badge variant="secondary" className="ml-2 whitespace-nowrap">
                            {item.portion}
                          </Badge>
                        </li>
                      ))}
                    </ul>
                    {meal.notes && (
                      <p className="text-sm text-gray-500 mt-3 pt-3 border-t italic">
                        {meal.notes}
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="border-0 shadow-md">
            <CardContent className="py-8 text-center">
              <AlertCircle className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-600">No meal plan for this day.</p>
            </CardContent>
          </Card>
        )}

        {/* General Notes */}
        {general_notes && general_notes.length > 0 && (
          <Card className="border-0 shadow-md mt-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Info className="w-4 h-4 text-blue-500" />
                Meal Plan Guidelines
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {general_notes.map((note, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                    <span className="text-green-500 mt-1">•</span>
                    <span>{note}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
